import { Link } from "react-router-dom";
import { useState } from "react";
import {
  ArrowUpRight, ArrowRight, Search, Map, Hammer, ClipboardCheck,
  Rocket, ShieldCheck, TrendingUp, Crown, ChevronRight, CheckCircle2,
} from "lucide-react";

const METHOD = [
  {
    key: "discover",
    n: "01",
    title: "Discover",
    tagline: "Understand before we advise.",
    icon: Search,
    blurb: "We begin by understanding your vision, goals, experience, target market and long term ambitions.",
    deliverables: [
      "Discovery Workshop",
      "Provider Readiness Assessment™",
      "Business Health Check",
      "Registration Pathway Assessment",
    ],
  },
  {
    key: "plan",
    n: "02",
    title: "Plan",
    tagline: "Create the roadmap.",
    icon: Map,
    blurb: "Before writing a single policy, we design your business.",
    deliverables: [
      "Business Strategy",
      "Service Offering",
      "Pricing Strategy",
      "Business Model",
      "Operational Roadmap",
      "Compliance Roadmap",
    ],
  },
  {
    key: "build",
    n: "03",
    title: "Build",
    tagline: "Build the business, not just the paperwork.",
    icon: Hammer,
    blurb: "This is where Elevate becomes different. We build the operating system of your organisation.",
    deliverables: [
      "Policies",
      "Procedures",
      "Governance Frameworks",
      "HR Systems",
      "Forms",
      "Registers",
      "Risk Management",
      "Quality Systems",
      "Operational Manuals",
    ],
  },
  {
    key: "register",
    n: "04",
    title: "Register",
    tagline: "Navigate registration with confidence.",
    icon: ClipboardCheck,
    blurb: "We manage the registration process from preparation through audit readiness.",
    deliverables: [
      "Application Support",
      "Documentation Review",
      "Audit Preparation",
      "Mock Audit",
      "Evidence Library",
      "Auditor Liaison Support",
    ],
  },
  {
    key: "launch",
    n: "05",
    title: "Launch",
    tagline: "Turn approval into a thriving business.",
    icon: Rocket,
    blurb: "Registration is the beginning. We help providers launch successfully and confidently.",
    deliverables: [
      "Client Onboarding Systems",
      "Service Agreements",
      "Website Review",
      "Referral Strategy",
      "Marketing Guidance",
      "Operations Review",
    ],
  },
  {
    key: "protect",
    n: "06",
    title: "Protect",
    tagline: "Stay compliant. Stay confident.",
    icon: ShieldCheck,
    badge: "Elevate Protect™",
    blurb: "Our ongoing compliance partnership keeps you ahead of the curve.",
    deliverables: [
      "Policy Updates",
      "Compliance Reviews",
      "Legislative Updates",
      "Internal Audits",
      "Governance Advice",
      "Unlimited Support (subject to membership terms)",
    ],
  },
  {
    key: "grow",
    n: "07",
    title: "Grow",
    tagline: "Build a stronger organisation.",
    icon: TrendingUp,
    blurb: "As your business grows, we support the next chapter.",
    deliverables: [
      "New Registration Groups",
      "Operational Efficiency",
      "Leadership Development",
      "Business Coaching",
      "Expansion Planning",
      "Team Development",
    ],
  },
  {
    key: "lead",
    n: "08",
    title: "Lead",
    tagline: "Create a lasting impact.",
    icon: Crown,
    blurb: "Build an organisation recognised for quality, leadership and participant outcomes.",
    deliverables: [
      "Governance Maturity",
      "Leadership Capability",
      "Organisational Culture",
      "Long term Growth Readiness",
    ],
  },
];

export default function Method() {
  const [active, setActive] = useState("discover");
  const stage = METHOD.find((m) => m.key === active);

  return (
    <div data-testid="method-page">
      {/* HERO */}
      <section className="relative bg-navy text-canvas overflow-hidden grain">
        <svg className="absolute -right-24 -top-10 w-[820px] h-[820px] opacity-25 pointer-events-none" viewBox="0 0 600 600" fill="none" aria-hidden>
          <defs>
            <linearGradient id="mswoosh" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"   stopColor="#163A66" />
              <stop offset="55%"  stopColor="#1F9AAE" />
              <stop offset="100%" stopColor="#5FD9E0" />
            </linearGradient>
          </defs>
          <path d="M 40 500 C 220 380, 380 260, 580 100" stroke="url(#mswoosh)" strokeWidth="5" strokeLinecap="round" />
        </svg>
        <div className="relative container-pad pt-24 pb-28">
          <div className="overline">Our methodology</div>
          <h1 className="font-display font-medium text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] mt-6 max-w-5xl">
            The <span className="text-brand-gradient font-serif-mark italic">Elevate Method™</span>
          </h1>
          <p className="font-display text-2xl sm:text-3xl mt-6 max-w-3xl text-canvas/90 leading-snug">
            We don't build registrations. We build successful providers.
          </p>
          <p className="text-canvas/75 mt-6 max-w-2xl leading-relaxed">
            An eight stage framework that takes providers from first idea to enduring leadership. Every Elevate client moves
            through it. It is the backbone of how we work and the reason our partnerships outlast registration.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid="method-hero-cta" className="btn-ochre">
              Start with Discover <ArrowUpRight size={18} />
            </Link>
            <a href="#stages" className="btn-light-on-navy">Explore the stages</a>
          </div>
        </div>
      </section>

      {/* STAGES STRIP */}
      <section id="stages" className="container-pad pt-20 pb-10">
        <div className="overline">The eight stages</div>
        <h2 className="font-display text-4xl sm:text-5xl text-navy mt-4 max-w-3xl tracking-tight leading-[1.08]">
          From discovery to leadership, one continuous journey.
        </h2>
      </section>

      {/* INTERACTIVE STAGE EXPLORER */}
      <section className="container-pad pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Stage navigation */}
          <aside className="lg:col-span-4">
            <div className="card-soft p-2 sticky top-28">
              <ul className="space-y-1">
                {METHOD.map((m) => {
                  const isActive = m.key === active;
                  return (
                    <li key={m.key}>
                      <button
                        data-testid={`method-stage-${m.key}`}
                        onClick={() => setActive(m.key)}
                        className={`w-full text-left flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                          isActive
                            ? "bg-brand-gradient text-canvas shadow-lg"
                            : "hover:bg-sand text-ink"
                        }`}
                      >
                        <span className={`font-display text-xl w-10 ${isActive ? "text-canvas" : "text-teal"}`}>{m.n}</span>
                        <span className="flex-1">
                          <div className={`font-display text-lg ${isActive ? "text-canvas" : "text-navy"}`}>{m.title}</div>
                          <div className={`text-xs mt-0.5 ${isActive ? "text-canvas/80" : "text-muted2"}`}>{m.tagline}</div>
                        </span>
                        {isActive ? null : <ChevronRight size={16} className="text-muted2" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          {/* Stage detail */}
          <div className="lg:col-span-8">
            <article key={stage.key} className="card-soft p-10 animate-fade-up">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="overline">Stage {stage.n}</div>
                  <h3 className="font-display text-4xl sm:text-5xl text-navy mt-3 tracking-tight">{stage.title}</h3>
                  <p className="font-serif-mark text-2xl text-teal italic mt-3">{stage.tagline}</p>
                </div>
                <div className="w-16 h-16 rounded-2xl bg-teal/10 flex items-center justify-center flex-shrink-0">
                  <stage.icon size={28} className="text-teal" />
                </div>
              </div>
              <p className="text-muted2 mt-6 leading-relaxed text-lg">{stage.blurb}</p>
              {stage.badge && (
                <div className="mt-6 inline-flex items-center gap-2 bg-teal/10 text-teal px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase">
                  {stage.badge}
                </div>
              )}
              <div className="mt-10">
                <div className="overline">Deliverables</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                  {stage.deliverables.map((d) => (
                    <div key={d} className="flex items-start gap-3 text-sm text-ink p-3 rounded-xl bg-sand">
                      <CheckCircle2 size={16} className="text-teal flex-shrink-0 mt-0.5" />
                      <span>{d}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-10 pt-8 border-t border-hairline flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid={`method-${stage.key}-cta`} className="btn-ochre">
                  Talk to a consultant <ArrowRight size={16} />
                </Link>
                <Link to="/services" className="btn-ghost-navy">
                  See related services
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* JOURNEY TIMELINE */}
      <section className="bg-sand py-24">
        <div className="container-pad">
          <div className="overline">The full journey</div>
          <h2 className="font-display text-4xl text-navy mt-4 max-w-2xl tracking-tight leading-[1.08]">
            One framework. Every stage. End to end clarity.
          </h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {METHOD.map((m, idx) => (
              <div key={m.key} className="card-soft p-5 text-center flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center">
                  <m.icon size={18} className="text-teal" />
                </div>
                <div className="font-display text-xs text-teal mt-3">{m.n}</div>
                <div className="font-display text-navy mt-1">{m.title}</div>
                {idx < METHOD.length - 1 && (
                  <ChevronRight size={14} className="text-teal/40 hidden lg:block absolute" style={{ position: 'relative', marginTop: 8 }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-pad py-24">
        <div className="bg-brand-gradient text-canvas rounded-3xl p-12 md:p-16 grain relative overflow-hidden">
          <div className="overline" style={{ color: '#A7E0E6' }}>Ready to begin?</div>
          <h3 className="font-display text-3xl sm:text-4xl mt-4 max-w-2xl tracking-tight leading-[1.1]">
            Every Elevate journey starts with Discover.
          </h3>
          <p className="text-canvas/80 mt-4 max-w-xl">
            Book a free 30 minute Discovery Workshop and we'll map your provider pathway together.
          </p>
          <Link to="/contact" data-testid="method-cta" className="btn-light-on-navy mt-8">
            Book my Discovery Workshop <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
